/**
 * Clears client-side caches after platform reset or session expiry.
 * HttpOnly cookies are not cleared here — server logout or expiry handles those.
 *
 * Keep locale key in sync with `frontend/src/i18n/config.ts` (STORAGE_KEY).
 */

const LOCALE_STORAGE_KEY = 'saee-locale';
const I18NEXT_LANG_KEY = 'i18nextLng';

function removeLocalStorageByPrefix(prefix: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(prefix)) keys.push(k);
  }
  keys.forEach((k) => window.localStorage.removeItem(k));
}

/**
 * Remove known app keys and any saee:* / auth_* drafts.
 */
export function clearPlatformStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(LOCALE_STORAGE_KEY);
    window.localStorage.removeItem(I18NEXT_LANG_KEY);
    removeLocalStorageByPrefix('saee:');
    removeLocalStorageByPrefix('auth_');

    const extra = ['saee-locale'];
    extra.forEach((k) => {
      try {
        window.localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    });

    // Any remaining keys matching auth_ (defensive full scan)
    const authLike: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('auth_')) authLike.push(k);
    }
    authLike.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore quota / private mode */
  }

  try {
    window.sessionStorage.clear();
  } catch {
    /* ignore */
  }

  if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
    void indexedDB.databases().then((dbs) => {
      dbs.forEach((db) => {
        if (db.name) {
          try {
            indexedDB.deleteDatabase(db.name);
          } catch {
            /* ignore */
          }
        }
      });
    });
  }
}
